import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import RiderShell, { RiderTopBar } from "@/components/rider/app/RiderShell";
import BikePathCard from "@/components/rider/app/BikePathCard";
import { RIDER_CATEGORIES } from "@/components/rider/app/categories";
import { Loader2, MapPin, Clock, Navigation, Compass, ChevronRight, Megaphone, Store, Route as RouteIcon } from "lucide-react";

export default function RiderHome() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [geo, setGeo] = useState("pending"); // pending | granted | fallback
  const [coords, setCoords] = useState(null);
  const [more, setMore] = useState(false);

  const load = async (lat, lng) => {
    const params = (lat != null && lng != null) ? { lat, lng } : {};
    setCoords(params);
    try { const { data } = await api.get("/rider/home", { params }); setData(data); }
    finally { setLoading(false); }
  };

  const showMore = async () => {
    setMore(true);
    try {
      const { data: next } = await api.get("/rider/home", { params: { ...coords, offset: data.bike_paths.length, limit: 5 } });
      setData((d) => ({ ...d, bike_paths: [...d.bike_paths, ...next.bike_paths], has_more: next.has_more, total: next.total }));
    } finally { setMore(false); }
  };

  const requestLocation = () => {
    if (!navigator.geolocation) { setGeo("fallback"); load(); return; }
    let done = false;
    navigator.geolocation.getCurrentPosition(
      (pos) => { done = true; setGeo("granted"); load(pos.coords.latitude, pos.coords.longitude); },
      () => { if (!done) { setGeo("fallback"); load(); } },
      { timeout: 6000, maximumAge: 300000 }
    );
  };
  useEffect(() => { requestLocation(); }, []);

  if (loading) return <RiderShell><div className="h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div></RiderShell>;

  const closest = data?.closest_bike_path;
  const others = (data?.bike_paths || []).filter((p) => p.id !== closest?.id);

  return (
    <RiderShell>
      <RiderTopBar />
      <div className="pb-10">
        {/* Hero: closest bike path */}
        {closest ? (
          <button onClick={() => nav(`/rider/bike-path/${closest.id}`)} data-testid="rider-hero" className="block w-full text-left relative">
            <div className="relative h-64 bg-secondary">
              {closest.cover_image ? (
                <img src={closest.cover_image} alt={closest.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-primary/40 via-primary/10 to-secondary" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/10" />
              <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 bg-primary text-primary-foreground text-[11px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full">
                <Navigation className="w-3 h-3" />{data.located ? "Closest to you" : "Featured path"}
              </div>
              <div className="absolute bottom-4 left-4 right-4">
                <h1 className="font-heading font-extrabold text-white text-2xl leading-tight drop-shadow">{closest.name}</h1>
                {closest.short_description && <p className="text-white/85 text-sm mt-1 line-clamp-2">{closest.short_description}</p>}
                <div className="flex items-center gap-3 mt-2 text-white/90 text-xs font-medium flex-wrap">
                  {closest.length_mi != null && <span className="inline-flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{closest.length_mi} mi</span>}
                  {closest.estimated_ride_time && <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" />{closest.estimated_ride_time}</span>}
                  {closest.distance_mi != null && <span className="bg-white/20 px-2 py-0.5 rounded-full">{closest.distance_mi < 0.1 ? "At trailhead" : `${closest.distance_mi} mi away`}</span>}
                </div>
              </div>
            </div>
          </button>
        ) : (
          <div className="p-8 text-center text-muted-foreground" data-testid="rider-no-paths">No bike paths available yet.</div>
        )}

        {geo === "fallback" && closest && (
          <button onClick={() => { setLoading(true); requestLocation(); }} data-testid="rider-enable-location"
            className="mx-4 mt-3 w-[calc(100%-2rem)] flex items-center gap-2 rounded-xl border border-primary/30 bg-primary/10 px-3 py-2 text-xs text-primary">
            <Compass className="w-4 h-4 shrink-0" />
            <span className="text-left">Showing a nearby path. Enable location for what's closest to you.</span>
            <ChevronRight className="w-4 h-4 ml-auto" />
          </button>
        )}

        {/* Counts chips */}
        {closest?.counts && (
          <div className="px-4 mt-4 grid grid-cols-3 gap-2" data-testid="rider-hero-counts">
            {[{ icon: RouteIcon, n: closest.counts.routes, l: "Routes" }, { icon: Megaphone, n: closest.counts.advertisers, l: "Places" }, { icon: Store, n: closest.counts.bike_shops, l: "Shops" }].map((c, i) => (
              <div key={i} className="rounded-xl bg-card border border-border py-2.5 text-center">
                <c.icon className="w-4 h-4 mx-auto text-primary" />
                <div className="font-heading font-bold text-lg leading-none mt-1">{c.n ?? 0}</div>
                <div className="text-[10px] uppercase tracking-wide text-muted-foreground mt-0.5">{c.l}</div>
              </div>
            ))}
          </div>
        )}

        {/* Category buttons */}
        {closest && (
          <div className="px-4 mt-6">
            <h2 className="font-heading font-bold text-sm uppercase tracking-wide text-muted-foreground mb-3">Explore along the trail</h2>
            <div className="grid grid-cols-3 gap-2.5" data-testid="rider-categories">
              {RIDER_CATEGORIES.map((c) => (
                <button key={c.key} onClick={() => nav(`/rider/bike-path/${closest.id}?cat=${c.key}`)} data-testid={`rider-cat-btn-${c.key}`}
                  className="aspect-square rounded-2xl bg-card border border-border flex flex-col items-center justify-center gap-2 active:scale-95 transition-transform">
                  <span className="w-11 h-11 rounded-full flex items-center justify-center" style={{ background: `${c.accent}22`, color: c.accent }}>
                    <c.icon className="w-5 h-5" />
                  </span>
                  <span className="text-xs font-semibold text-center leading-tight px-1">{c.label}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Other bike paths */}
        {others.length > 0 && (
          <div className="px-4 mt-7">
            <h2 className="font-heading font-bold text-sm uppercase tracking-wide text-muted-foreground mb-3">{data.located ? "More paths near you" : "All bike paths"}</h2>
            <div className="space-y-3" data-testid="rider-path-list">
              {others.map((p) => <BikePathCard key={p.id} p={p} onClick={() => nav(`/rider/bike-path/${p.id}`)} />)}
            </div>
            {data.has_more && (
              <button onClick={showMore} disabled={more} data-testid="rider-show-more"
                className="mt-4 w-full rounded-xl border border-border bg-card py-3 text-sm font-semibold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform disabled:opacity-60">
                {more && <Loader2 className="w-4 h-4 animate-spin" />}Show more
                <span className="text-xs font-normal text-muted-foreground">({data.bike_paths.length} of {data.total})</span>
              </button>
            )}
          </div>
        )}
      </div>
    </RiderShell>
  );
}
